import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Input, Spin, message, Form, TreeSelect, Button, Tree, Card, Space, Typography } from 'antd';
import { MinusSquareOutlined, PlusSquareOutlined } from '@ant-design/icons';
import { gql, useMutation, useQuery, useLazyQuery } from '@apollo/client';
import { SiteType } from 'graphql/types/SiteType';
import { LIST_ALL_DEPTS, type DeptFlatType, type ListAllDeptsResponse } from './graphql/queries/listAllDepts';
import { GET_DEPT_DETAIL_BY_ID, type GetDeptDetailByIdResponse } from './graphql/queries/getDeptDetailById';
import {
  ADD_DEPT,
  UPDATE_DEPT,
  TRANSFER_DEPT,
  DELETE_DEPT,
} from './graphql/mutations/deptMutations';
import { getPlatformPermissionErrorMessage } from '../utils/errorCode';
import { HasResource } from '../../../../components/HasResource';
import { ResourceCode } from 'constants/resourceCodes';

const GET_WAREHOUSE_LIST_FOR_ORG = gql`
  query GetWarehouseListForOrg {
    warehouseList: getWarehouseList {
      id
      name
      parentSiteId
      siteType
    }
  }
`;

interface OrgWarehouseType {
  id: string;
  name: string;
  parentSiteId?: string | null;
  siteType: number;
}

interface GetWarehouseListForOrgResponse {
  warehouseList: OrgWarehouseType[];
}

const ALL_WAREHOUSE_VALUE = 0;
type HubSelectorState = {
  hubIds: Set<number>;
  allowAll: boolean;
  noScope: boolean;
  showChildren: boolean;
  /** 当上级仅选择了 WHS（无 hub/无 all）时，限制子集仅显示这些 WHS */
  whsOnlyIds?: Set<number>;
};

export interface OrgNodeData {
  id: string;
  name: string;
  fullName?: string;
  warehouseList?: number[];
  children?: OrgNodeData[];
}

const ROOT_ID = 'root';
const ROOT_DEPT_NUMERIC_ID = 1;

function toOrgNode(dept: DeptFlatType): OrgNodeData {
  return {
    id: String(dept.id),
    name: String(dept.name ?? ''),
    fullName: dept.fullName ? String(dept.fullName) : String(dept.name ?? ''),
    warehouseList: (dept.warehouseList ?? []).map((x) => Number(x)),
    children: [],
  };
}

function buildOrgTreeFromFlat(depts: DeptFlatType[]): OrgNodeData {
  const nodeMap = new Map<string, OrgNodeData>();
  const parentIdMap = new Map<string, string | null>();
  const allNodes = depts.map((dept) => {
    const node = toOrgNode(dept);
    nodeMap.set(node.id, node);
    const p = dept.parentId == null ? null : String(dept.parentId);
    parentIdMap.set(node.id, p);
    return node;
  });

  const roots: OrgNodeData[] = [];
  allNodes.forEach((node) => {
    const parentId = parentIdMap.get(node.id);
    const parent = parentId ? nodeMap.get(parentId) : null;
    if (parent) {
      parent.children = parent.children ?? [];
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  });

  const backendRoot = nodeMap.get(String(ROOT_DEPT_NUMERIC_ID)) ?? null;
  if (backendRoot) {
    const otherRoots = roots.filter((n) => n.id !== String(ROOT_DEPT_NUMERIC_ID));
    return {
      id: ROOT_ID,
      name: backendRoot.name || 'SwiftX',
      warehouseList: backendRoot.warehouseList,
      children: [...(backendRoot.children ?? []), ...otherRoots],
    };
  }

  return {
    id: ROOT_ID,
    name: 'SwiftX',
    children: roots,
  };
}

function findNodeById(tree: OrgNodeData, id: string): OrgNodeData | null {
  if (tree.id === id) return tree;
  for (const c of tree.children ?? []) {
    const found = findNodeById(c, id);
    if (found) return found;
  }
  return null;
}

function findParentId(tree: OrgNodeData, childId: string): string | null {
  for (const c of tree.children ?? []) {
    if (c.id === childId) return tree.id;
    const found = findParentId(c, childId);
    if (found) return found;
  }
  return null;
}

function getDescendantIds(tree: OrgNodeData, includeSelf: boolean): Set<string> {
  const set = new Set<string>();
  if (includeSelf) set.add(tree.id);
  (tree.children ?? []).forEach((c) => {
    getDescendantIds(c, true).forEach((id) => set.add(id));
  });
  return set;
}

type TransferTreeOption = {
  title: React.ReactNode;
  searchText: string;
  value: string;
  key: string;
  children?: TransferTreeOption[];
};

const renderPlusMinusSwitcher = (nodeProps: { expanded?: boolean; isLeaf?: boolean }) => {
  if (nodeProps.isLeaf) return null;
  return nodeProps.expanded ? <MinusSquareOutlined /> : <PlusSquareOutlined />;
};

function buildTransferTreeOptions(
  tree: OrgNodeData,
  excludeIds: Set<string>,
  renderTitle: (node: OrgNodeData) => React.ReactNode
): TransferTreeOption[] {
  function walk(node: OrgNodeData): TransferTreeOption | null {
    if (excludeIds.has(node.id)) return null;
    const children = (node.children ?? [])
      .map(walk)
      .filter((x): x is TransferTreeOption => x != null);
    const plainText = node.id === ROOT_ID ? node.name : `${node.name}(${node.id})`;
    return {
      title: renderTitle(node),
      searchText: plainText,
      value: node.id,
      key: node.id,
      children: children.length > 0 ? children : undefined,
    };
  }
  const root = walk(tree);
  return root ? [root] : [];
}

function parentIdToNumeric(parentIdStr: string | null): number {
  if (parentIdStr === null || parentIdStr === ROOT_ID) return ROOT_DEPT_NUMERIC_ID;
  return Number(parentIdStr);
}

type ModalType = 'rename' | 'transfer' | 'delete' | 'add' | null;

const OrganizationStructurePage: React.FC = () => {
  const { t } = useTranslation();
  const { data: deptFlatData, loading, refetch } = useQuery<ListAllDeptsResponse>(LIST_ALL_DEPTS, {
    fetchPolicy: 'network-only',
  });

  const [loadDeptDetail, { data: detailData }] = useLazyQuery<GetDeptDetailByIdResponse>(GET_DEPT_DETAIL_BY_ID, {
    fetchPolicy: 'network-only',
  });
  const [loadWarehouseList, { data: warehouseListData, loading: warehouseListLoading }] = useLazyQuery<GetWarehouseListForOrgResponse>(GET_WAREHOUSE_LIST_FOR_ORG, {
    fetchPolicy: 'network-only',
  });

  const [addDept] = useMutation(ADD_DEPT);
  const [updateDept] = useMutation(UPDATE_DEPT);
  const [transferDept] = useMutation(TRANSFER_DEPT);
  const [deleteDept] = useMutation(DELETE_DEPT);

  const treeData: OrgNodeData = useMemo(() => {
    const depts = deptFlatData?.listAllDepts ?? [];
    return buildOrgTreeFromFlat(depts);
  }, [deptFlatData]);

  const [modal, setModal] = useState<{ type: ModalType; nodeId: string } | null>(null);
  const [renameForm] = Form.useForm();
  const [addForm] = Form.useForm();
  const [transferTargetId, setTransferTargetId] = useState<string | null>(null);
  const [selectedWarehouseIds, setSelectedWarehouseIds] = useState<number[]>([]);
  const [warehouseExpandedKeys, setWarehouseExpandedKeys] = useState<Array<string | number>>([]);
  const [transferTreeExpandedKeys, setTransferTreeExpandedKeys] = useState<string[]>([]);
  const [transferTreeSearch, setTransferTreeSearch] = useState('');
  const [mutating, setMutating] = useState(false);
  const [treeExpandedKeys, setTreeExpandedKeys] = useState<string[]>([]);
  const [treeSelectedKeys, setTreeSelectedKeys] = useState<string[]>([]);
  const [treeSearch, setTreeSearch] = useState('');

  useEffect(() => {
    if (!modal || modal.nodeId === ROOT_ID) return;
    if (modal.type === 'rename' || modal.type === 'transfer' || modal.type === 'delete') {
      void loadDeptDetail({ variables: { id: Number(modal.nodeId) } });
    }
  }, [modal, loadDeptDetail]);

  useEffect(() => {
    if (!modal) return;
    if (modal.type === 'add' || modal.type === 'rename' || modal.type === 'transfer') {
      void loadWarehouseList();
    }
  }, [modal, loadWarehouseList]);

  const currentNode = modal?.nodeId ? findNodeById(treeData, modal.nodeId) : null;
  const currentParentId = modal?.nodeId ? findParentId(treeData, modal.nodeId) : null;
  const currentParentNode = currentParentId ? findNodeById(treeData, currentParentId) : null;
  const warehouseSites = useMemo(() => warehouseListData?.warehouseList ?? [], [warehouseListData]);

  const hubWarehouseIdSet = useMemo(() => {
    const set = new Set<number>();
    warehouseSites.forEach((site) => {
      if (Number(site.siteType) === SiteType.HUB) set.add(Number(site.id));
    });
    return set;
  }, [warehouseSites]);

  const expandScopeWithChildren = useCallback(
    (baseIds: Set<number>): Set<number> => {
      if (baseIds.size === 0) return new Set<number>();
      const result = new Set<number>(baseIds);
      warehouseSites.forEach((site) => {
        const parentId = site.parentSiteId != null ? Number(site.parentSiteId) : null;
        if (parentId != null && baseIds.has(parentId)) {
          result.add(Number(site.id));
        }
      });
      return result;
    },
    [warehouseSites]
  );

  const getNodeChainFromRoot = useCallback(
    (nodeId: string): OrgNodeData[] => {
      const chain: OrgNodeData[] = [];
      let cursorId: string | null = nodeId;
      while (cursorId) {
        if (cursorId === ROOT_ID) {
          chain.unshift(treeData);
          break;
        }
        const node = findNodeById(treeData, cursorId);
        if (!node) break;
        chain.unshift(node);
        cursorId = findParentId(treeData, cursorId);
      }
      return chain;
    },
    [treeData]
  );

  const getScopedHubSelectorByParent = useCallback(
    (parentNodeId: string): HubSelectorState => {
      if (parentNodeId === ROOT_ID) {
        return { hubIds: new Set<number>(hubWarehouseIdSet), allowAll: true, noScope: false, showChildren: false };
      }
      const chain = getNodeChainFromRoot(parentNodeId);
      let hasAnySelection = false;
      let hasBreak = false;
      let lastNonEmptySelection: number[] = [];

      chain.forEach((node) => {
        const list = (node.warehouseList ?? []).map((x) => Number(x));
        if (list.length === 0) {
          if (hasAnySelection) hasBreak = true;
          return;
        }
        hasAnySelection = true;
        lastNonEmptySelection = list;
      });

      if (hasBreak) return { hubIds: new Set<number>(), allowAll: false, noScope: true, showChildren: false };
      if (!hasAnySelection) {
        // 上级链路均为空：允许“全部 + 所有Hub”
        return { hubIds: new Set<number>(hubWarehouseIdSet), allowAll: true, noScope: false, showChildren: false };
      }

      // 以“最后一次非空选择”为准决定下一级范围
      if (lastNonEmptySelection.includes(ALL_WAREHOUSE_VALUE)) {
        // 上级选了“全部”：下级保持与上级一致（继续可选“全部”），
        // 直到某一级不再选择“全部”后，再按既有Hub/WHS规则收敛范围。
        return { hubIds: new Set<number>(hubWarehouseIdSet), allowAll: true, noScope: false, showChildren: false };
      }

      const selectedHubIds = new Set<number>(lastNonEmptySelection.filter((id) => hubWarehouseIdSet.has(id)));
      if (selectedHubIds.size > 0) {
        // 上级选了Hub（全选或部分）：下级显示这些Hub + Hub下WHS
        return { hubIds: selectedHubIds, allowAll: false, noScope: false, showChildren: true };
      }

      // 上级只选了WHS：下级严格继承这些WHS（不放大，不补父Hub）
      const whsOnlyIds = new Set<number>(lastNonEmptySelection);
      return { hubIds: new Set<number>(), allowAll: false, noScope: false, showChildren: false, whsOnlyIds };
    },
    [getNodeChainFromRoot, hubWarehouseIdSet]
  );

  const warehouseContextParentId = useMemo((): string | null => {
    if (!modal) return null;
    if (modal.type === 'add') return modal.nodeId;
    if (modal.type === 'rename') return currentParentId ?? ROOT_ID;
    return null;
  }, [modal, currentParentId]);

  const warehouseHubSelector = useMemo<HubSelectorState>(() => {
    if (!warehouseContextParentId) return { hubIds: new Set<number>(), allowAll: false, noScope: false, showChildren: false };
    return getScopedHubSelectorByParent(warehouseContextParentId);
  }, [warehouseContextParentId, getScopedHubSelectorByParent]);

  const warehouseScopeForModal = useMemo(() => {
    if (modal?.type !== 'add' && modal?.type !== 'rename') return new Set<number>();
    if (warehouseHubSelector.whsOnlyIds && warehouseHubSelector.whsOnlyIds.size > 0) {
      return new Set<number>(warehouseHubSelector.whsOnlyIds);
    }
    if (warehouseHubSelector.showChildren) {
      return expandScopeWithChildren(new Set<number>(warehouseHubSelector.hubIds));
    }
    return new Set<number>(warehouseHubSelector.hubIds);
  }, [modal?.type, warehouseHubSelector.hubIds, warehouseHubSelector.showChildren, warehouseHubSelector.whsOnlyIds, expandScopeWithChildren]);

  const warehouseTreeDataForModal = useMemo(() => {
    if (modal?.type !== 'add' && modal?.type !== 'rename') return [];
    if (warehouseHubSelector.noScope || warehouseScopeForModal.size === 0) return [];
    if (warehouseHubSelector.whsOnlyIds && warehouseHubSelector.whsOnlyIds.size > 0) {
      // 仅WHS继承场景：平铺展示父级已选WHS，不展示父Hub分组
      return warehouseSites
        .filter((site) => warehouseHubSelector.whsOnlyIds!.has(Number(site.id)))
        .map((site) => ({
          title: site.name,
          value: Number(site.id),
          key: Number(site.id),
          selectable: true,
          disableCheckbox: false,
        }));
    }
    const hubNodes = warehouseSites
      .filter((site) => Number(site.siteType) === SiteType.HUB && warehouseHubSelector.hubIds.has(Number(site.id)))
      .map((hub) => {
        const hubId = Number(hub.id);
        return {
          title: hub.name,
          value: hubId,
          key: hubId,
          selectable: true,
          disableCheckbox: false,
          children: warehouseHubSelector.showChildren
            ? warehouseSites
                .filter((site) => site.parentSiteId != null && Number(site.parentSiteId) === hubId)
                .filter((site) => (warehouseHubSelector.whsOnlyIds ? warehouseHubSelector.whsOnlyIds.has(Number(site.id)) : true))
                .map((child) => ({
                  title: child.name,
                  value: Number(child.id),
                  key: Number(child.id),
                  selectable: true,
                  disableCheckbox: false,
                }))
            : undefined,
        };
      });

    if (warehouseHubSelector.allowAll) {
      return [
        {
          title: String(t('central.platformPermission.organization.modal.all')),
          value: ALL_WAREHOUSE_VALUE,
          key: ALL_WAREHOUSE_VALUE,
          selectable: true,
          disableCheckbox: false,
          children: hubNodes,
        },
      ];
    }
    return hubNodes;
  }, [modal?.type, warehouseHubSelector.noScope, warehouseHubSelector.allowAll, warehouseHubSelector.showChildren, warehouseHubSelector.hubIds, warehouseHubSelector.whsOnlyIds, warehouseSites, t]);

  const allWarehouseTreeKeys = useMemo(() => {
    const keys: Array<string | number> = [];
    const walk = (nodes: any[]) => {
      nodes.forEach((n) => {
        if (n?.key != null && (typeof n.key === 'string' || typeof n.key === 'number')) keys.push(n.key);
        if (Array.isArray(n?.children) && n.children.length > 0) walk(n.children);
      });
    };
    walk(warehouseTreeDataForModal as any[]);
    return keys;
  }, [warehouseTreeDataForModal]);

  useEffect(() => {
    if (modal?.type !== 'add' && modal?.type !== 'rename') return;
    if (allWarehouseTreeKeys.length === 0) return;
    setWarehouseExpandedKeys(allWarehouseTreeKeys);
  }, [modal?.type, allWarehouseTreeKeys]);

  const warehouseSelectPlaceholder = useMemo(() => {
    if ((modal?.type === 'add' || modal?.type === 'rename') && warehouseTreeDataForModal.length === 0) {
      return String(t('central.platformPermission.organization.modal.parentWarehouseNotConfigured'));
    }
    return String(t('components.input.select'));
  }, [modal?.type, warehouseTreeDataForModal.length, t]);

  const normalizeWarehouseSelection = useCallback(
    (rawValues: number[]): number[] => {
      if (rawValues.includes(ALL_WAREHOUSE_VALUE)) return [ALL_WAREHOUSE_VALUE];
      return rawValues.filter((id) => warehouseScopeForModal.has(id));
    },
    [warehouseScopeForModal]
  );

  const handleWarehouseTreeChange = useCallback(
    (vals: Array<number | string | { value: number | string }>) => {
      const arr = vals
        .map((x) => (typeof x === 'object' && x !== null ? x.value : x))
        .map((x) => Number(x))
        .filter((x) => Number.isFinite(x));
      setSelectedWarehouseIds(normalizeWarehouseSelection(arr));
    },
    [normalizeWarehouseSelection]
  );

  const renderTransferHighlightedName = useCallback((name: string) => {
    const rawName = String(name ?? '');
    const keyword = transferTreeSearch.trim();
    if (!keyword) return rawName;
    const lowerName = rawName.toLowerCase();
    const lowerKeyword = keyword.toLowerCase();
    const matchIndex = lowerName.indexOf(lowerKeyword);
    if (matchIndex < 0) return rawName;
    const before = rawName.slice(0, matchIndex);
    const hit = rawName.slice(matchIndex, matchIndex + keyword.length);
    const after = rawName.slice(matchIndex + keyword.length);
    return (
      <>
        {before}
        <span className="text-purple-700 font-medium">{hit}</span>
        {after}
      </>
    );
  }, [transferTreeSearch]);

  const transferTreeOptions = useMemo(() => {
    if (modal?.type !== 'transfer' || !currentNode) return [];
    const excludeIds = getDescendantIds(currentNode, true);
    return buildTransferTreeOptions(
      treeData,
      excludeIds,
      (node) => renderTransferHighlightedName(node.id === ROOT_ID ? node.name : `${node.name}(${node.id})`)
    );
  }, [modal?.type, modal?.nodeId, currentNode, treeData, renderTransferHighlightedName]);

  const openRename = useCallback(
    (id: string) => {
      const n = findNodeById(treeData, id);
      if (n) {
        renameForm.setFieldsValue({ name: n.name });
        setSelectedWarehouseIds((n.warehouseList ?? []).map((x) => Number(x)));
        setModal({ type: 'rename', nodeId: id });
      }
    },
    [treeData, renameForm]
  );

  const openTransfer = useCallback((id: string) => {
    setTransferTargetId(null);
    setTransferTreeExpandedKeys([ROOT_ID]);
    setTransferTreeSearch('');
    const n = findNodeById(treeData, id);
    setSelectedWarehouseIds((n?.warehouseList ?? []).map((x) => Number(x)));
    setModal({ type: 'transfer', nodeId: id });
  }, [treeData]);

  const openDelete = useCallback((id: string) => {
    setModal({ type: 'delete', nodeId: id });
  }, []);

  const openAdd = useCallback((parentId: string) => {
    addForm.resetFields();
    addForm.setFieldsValue({ name: '' });
    setSelectedWarehouseIds([]);
    setModal({ type: 'add', nodeId: parentId });
  }, [addForm]);

  const closeModal = () => {
    renameForm.resetFields();
    addForm.resetFields();
    setWarehouseExpandedKeys([]);
    setTransferTreeExpandedKeys([]);
    setTransferTreeSearch('');
    setModal(null);
  };

  useEffect(() => {
    if (!modal) return;
    if (modal.type === 'rename' || modal.type === 'transfer') {
      const fromDetail = (detailData?.getDeptDetailById?.data?.warehouseList ?? []).map((x) => Number(x));
      if (fromDetail.length > 0 || detailData?.getDeptDetailById?.data?.warehouseList?.length === 0) {
        if (modal.type === 'rename') {
          setSelectedWarehouseIds(normalizeWarehouseSelection(fromDetail));
        } else {
          setSelectedWarehouseIds(fromDetail);
        }
      }
    }
  }, [modal, detailData, normalizeWarehouseSelection]);

  const confirmRename = async () => {
    if (modal?.type !== 'rename' || !modal) return;
    try {
      const values = await renameForm.validateFields();
      const renameName = String(values.name ?? '').trim();
      const parentIdStr = findParentId(treeData, modal.nodeId);
      const parentIdNum = parentIdToNumeric(parentIdStr);
      const wh = selectedWarehouseIds;
      setMutating(true);
      const res = await updateDept({
        variables: {
          input: {
            id: Number(modal.nodeId),
            name: renameName,
            parentId: parentIdNum,
            warehouseList: wh,
          },
        },
      });
      const r = res.data?.updateDept;
      if (r?.success) {
        const toastText = getPlatformPermissionErrorMessage(t, r?.messageCode, r?.message);
        if (toastText) message.success(toastText);
        closeModal();
        await refetch();
      } else {
        const toastText = getPlatformPermissionErrorMessage(t, r?.messageCode, r?.message);
        if (toastText) message.error(toastText);
      }
    } catch (e: any) {
      if (Array.isArray(e?.errorFields)) return;
      const toastText = getPlatformPermissionErrorMessage(t, undefined, e?.message);
      if (toastText) message.error(toastText);
    } finally {
      setMutating(false);
    }
  };

  const confirmTransfer = async () => {
    if (modal?.type !== 'transfer' || transferTargetId == null || !currentNode || !modal) return;
      const wh = normalizeWarehouseSelection(selectedWarehouseIds);
    const newParentId = transferTargetId === ROOT_ID ? ROOT_DEPT_NUMERIC_ID : Number(transferTargetId);
    setMutating(true);
    try {
      const res = await transferDept({
        variables: {
          input: {
            id: Number(modal.nodeId),
            name: currentNode.name,
            parentId: newParentId,
            warehouseList: wh,
          },
        },
      });
      const r = res.data?.transferDept;
      if (r?.success) {
        const toastText = getPlatformPermissionErrorMessage(t, r?.messageCode, r?.message);
        if (toastText) message.success(toastText);
        closeModal();
        await refetch();
      } else {
        const toastText = getPlatformPermissionErrorMessage(t, r?.messageCode, r?.message);
        if (toastText) message.error(toastText);
      }
    } catch (e: any) {
      const toastText = getPlatformPermissionErrorMessage(t, undefined, e?.message);
      if (toastText) message.error(toastText);
    } finally {
      setMutating(false);
    }
  };

  const confirmDelete = async () => {
    if (modal?.type !== 'delete' || !modal) return;
    if (modal.nodeId === ROOT_ID) return;
    setMutating(true);
    try {
      const res = await deleteDept({
        variables: {
          input: {
            id: Number(modal.nodeId),
            name: '',
            parentId: ROOT_DEPT_NUMERIC_ID,
            warehouseList: normalizeWarehouseSelection(selectedWarehouseIds),
          },
        },
      });
      const r = res.data?.deleteDept;
      if (r?.success) {
        const toastText = getPlatformPermissionErrorMessage(t, r?.messageCode, r?.message);
        if (toastText) message.success(toastText);
        closeModal();
        await refetch();
      } else {
        const toastText = getPlatformPermissionErrorMessage(t, r?.messageCode, r?.message);
        if (toastText) message.error(toastText);
      }
    } catch (e: any) {
      const toastText = getPlatformPermissionErrorMessage(t, undefined, e?.message);
      if (toastText) message.error(toastText);
    } finally {
      setMutating(false);
    }
  };

  const confirmAdd = async () => {
    if (modal?.type !== 'add' || !modal) return;
    try {
      const values = await addForm.validateFields();
      const name = String(values.name ?? '').trim();
      const parentIdNum = modal.nodeId === ROOT_ID ? ROOT_DEPT_NUMERIC_ID : Number(modal.nodeId);
      setMutating(true);
      const res = await addDept({
        variables: {
          input: {
            name,
            parentId: parentIdNum,
            warehouseList: selectedWarehouseIds,
          },
        },
      });
      const r = res.data?.addDept;
      if (r?.success) {
        if (modal.nodeId !== ROOT_ID) {
          setTreeExpandedKeys((prev) => Array.from(new Set([...prev, modal.nodeId])));
        }
        const toastText = getPlatformPermissionErrorMessage(t, r?.messageCode, r?.message);
        if (toastText) message.success(toastText);
        closeModal();
        await refetch();
      } else {
        const toastText = getPlatformPermissionErrorMessage(t, r?.messageCode, r?.message);
        if (toastText) message.error(toastText);
      }
    } catch (e: any) {
      if (Array.isArray(e?.errorFields)) return;
      const toastText = getPlatformPermissionErrorMessage(t, undefined, e?.message);
      if (toastText) message.error(toastText);
    } finally {
      setMutating(false);
    }
  };

  const treeTitle = String(t('central.platformPermission.organizationStructureMenu'));

  const renderHighlightedName = useCallback((name: string) => {
    const rawName = String(name ?? '');
    const keyword = treeSearch.trim();
    if (!keyword) return rawName;
    const lowerName = rawName.toLowerCase();
    const lowerKeyword = keyword.toLowerCase();
    const matchIndex = lowerName.indexOf(lowerKeyword);
    if (matchIndex < 0) return rawName;
    const before = rawName.slice(0, matchIndex);
    const hit = rawName.slice(matchIndex, matchIndex + keyword.length);
    const after = rawName.slice(matchIndex + keyword.length);
    return (
      <>
        {before}
        <span className="text-purple-700 font-medium">{hit}</span>
        {after}
      </>
    );
  }, [treeSearch]);

  const treeDataForAntd = useMemo(() => {
    const build = (node: OrgNodeData): any | null => {
      const children = (node.children ?? [])
        .map(build)
        .filter((x) => x != null);
      return {
        key: node.id,
        title: (
          <div className="flex items-center justify-between gap-3 min-w-0">
            <div className="min-w-0">
              <div className="truncate text-[14px] text-[rgba(0,0,0,0.88)]">{renderHighlightedName(node.name)}</div>
              {node.id !== ROOT_ID && <div className="truncate text-[12px] text-[rgba(0,0,0,0.45)]">ID: {node.id}</div>}
            </div>
            {node.id !== ROOT_ID && (
              <Space size={12} onClick={(e) => e.stopPropagation()}>
                <HasResource resourceCodes={[ResourceCode.dept.UPDATE]}>
                  <a
                    className="text-[12px] text-purple-700"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openRename(node.id);
                    }}
                  >
                    {t('central.platformPermission.organization.edit')}
                  </a>
                </HasResource>
                <HasResource resourceCodes={[ResourceCode.dept.TRANSFER]}>
                  <a
                    className="text-[12px] text-purple-700"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openTransfer(node.id);
                    }}
                  >
                    {t('central.platformPermission.organization.transfer')}
                  </a>
                </HasResource>
                <HasResource resourceCodes={[ResourceCode.dept.ADD]}>
                  <a
                    className="text-[12px] text-purple-700"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openAdd(node.id);
                    }}
                  >
                    {t('central.platformPermission.organization.add')}
                  </a>
                </HasResource>
                <HasResource resourceCodes={[ResourceCode.dept.DELETE]}>
                  <a
                    className="text-[12px] text-red-500 hover:!text-red-400"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openDelete(node.id);
                    }}
                  >
                    {t('central.platformPermission.organization.delete')}
                  </a>
                </HasResource>
              </Space>
            )}
          </div>
        ),
        children,
      };
    };
    const roots = (treeData.children ?? [])
      .map(build)
      .filter((x) => x != null);
    return roots;
  }, [treeData, renderHighlightedName, openAdd, openDelete, openRename, openTransfer, t]);

  useEffect(() => {
    const keyword = treeSearch.trim().toLowerCase();
    if (!keyword) return;
    const keysToExpand = new Set<string>();
    const walk = (node: OrgNodeData): boolean => {
      const selfMatch = String(node.name ?? '').toLowerCase().includes(keyword);
      let descendantMatch = false;
      (node.children ?? []).forEach((child) => {
        if (walk(child)) descendantMatch = true;
      });
      if (descendantMatch) keysToExpand.add(node.id);
      return selfMatch || descendantMatch;
    };
    walk(treeData);
    setTreeExpandedKeys(Array.from(keysToExpand));
  }, [treeSearch, treeData]);

  useEffect(() => {
    if (modal?.type !== 'transfer' || !currentNode) return;
    const keyword = transferTreeSearch.trim().toLowerCase();
    if (!keyword) return;
    const excludeIds = getDescendantIds(currentNode, true);
    const keysToExpand = new Set<string>();
    const walk = (node: OrgNodeData): boolean => {
      if (excludeIds.has(node.id)) return false;
      const selfMatch = String(node.name ?? '').toLowerCase().includes(keyword);
      let descendantMatch = false;
      (node.children ?? []).forEach((child) => {
        if (walk(child)) descendantMatch = true;
      });
      if (descendantMatch) keysToExpand.add(node.id);
      return selfMatch || descendantMatch;
    };
    walk(treeData);
    setTransferTreeExpandedKeys(Array.from(keysToExpand));
  }, [modal?.type, currentNode, transferTreeSearch, treeData]);

  useEffect(() => {
    if (treeSelectedKeys.length > 0) return;
    const firstChildId = treeData.children?.[0]?.id;
    if (firstChildId) setTreeSelectedKeys([firstChildId]);
  }, [treeData, treeSelectedKeys.length]);

  const collectDescendantKeys = useCallback((nodeId: string): string[] => {
    const startNode = findNodeById(treeData, nodeId);
    if (!startNode) return [];
    const keys: string[] = [];
    const walk = (node: OrgNodeData) => {
      (node.children ?? []).forEach((child) => {
        keys.push(child.id);
        walk(child);
      });
    };
    walk(startNode);
    return keys;
  }, [treeData]);

  const baseModalProps = {
    onCancel: closeModal,
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <Typography.Title level={4} className="!mb-4">
        {treeTitle}
      </Typography.Title>

      <Spin spinning={loading}>
        <div>
          <div>
            <Card
              size="small"
              title={
                <div className="flex items-center justify-between gap-3">
                  <div className="font-medium text-[rgba(0,0,0,0.88)] truncate">{treeData.name}</div>
                  <HasResource resourceCodes={[ResourceCode.dept.ADD]}>
                    <Button
                      type="primary"
                      className="bg-purple-600 hover:!bg-purple-700 border-purple-600"
                      onClick={() => openAdd(ROOT_ID)}
                    >
                      {t('central.platformPermission.organization.add')}
                    </Button>
                  </HasResource>
                </div>
              }
              bodyStyle={{ padding: 12 }}
            >
              <div className="mb-3">
                <Input
                  allowClear
                  value={treeSearch}
                  onChange={(e) => setTreeSearch(e.target.value)}
                  placeholder={String(t('components.input.typeIn'))}
                />
              </div>
              <Tree
                blockNode
                showLine={{ showLeafIcon: false }}
                switcherIcon={renderPlusMinusSwitcher}
                treeData={treeDataForAntd}
                expandedKeys={treeExpandedKeys}
                selectedKeys={treeSelectedKeys}
                onExpand={(keys) => {
                  const nextSet = new Set((keys as string[]).map(String));
                  const prevSet = new Set(treeExpandedKeys);
                  const collapsedParents = Array.from(prevSet).filter((k) => !nextSet.has(k));
                  collapsedParents.forEach((parentKey) => {
                    collectDescendantKeys(parentKey).forEach((descendantKey) => {
                      nextSet.delete(descendantKey);
                    });
                  });
                  setTreeExpandedKeys(Array.from(nextSet));
                }}
                onSelect={(keys) => setTreeSelectedKeys(keys as string[])}
              />
            </Card>
          </div>
        </div>
      </Spin>

      <Modal
        {...baseModalProps}
        title={String(t('central.platformPermission.organization.modal.renameTitle'))}
        open={modal?.type === 'rename'}
        footer={
          <div className="flex justify-end gap-2">
            <Button className="text-gray-700" onClick={closeModal}>
              {String(t('central.platformPermission.organization.modal.cancel'))}
            </Button>
            <HasResource resourceCodes={[ResourceCode.dept.UPDATE]}>
              <Button
                type="primary"
                className="bg-purple-600 hover:!bg-purple-700 border-purple-600"
                loading={mutating}
                onClick={confirmRename}
              >
                {String(t('central.platformPermission.organization.modal.renameConfirm'))}
              </Button>
            </HasResource>
          </div>
        }
      >
        {currentNode && (
          <Form form={renameForm} component={false}>
            <div className="space-y-3 pt-2 max-w-[520px] mx-auto">
            <div className="flex flex-row items-center gap-2">
              <label className="text-sm text-black shrink-0 w-28 text-right">ID:</label>
              <div className="text-sm text-black">{currentNode.id}</div>
            </div>
            <div className="flex flex-row items-center gap-2">
              <label className="text-sm text-black shrink-0 w-28 text-right">
                {t('central.platformPermission.organization.modal.renameCurrentName')}:
              </label>
              <div className="text-sm text-black">{currentNode.name}</div>
            </div>
            <div className="flex flex-row items-start gap-2">
              <label className="text-sm text-black shrink-0 w-28 text-right pt-[6px]">
                {t('central.platformPermission.organization.modal.renameNewName')}:
              </label>
              <Form.Item
                name="name"
                rules={[{ required: true, message: String(t('central.platformPermission.organization.modal.renameNameRequired')) }]}
                className="mb-0 flex-1 min-w-0"
              >
                <Input
                  maxLength={100}
                  onBlur={() => {
                    const val = String(renameForm.getFieldValue('name') ?? '').trim();
                    renameForm.setFieldsValue({ name: val });
                  }}
                  placeholder={String(t('components.input.typeIn'))}
                  className="rounded border-purple-200"
                />
              </Form.Item>
            </div>
            <div className="flex flex-row items-center gap-2">
              <label className="text-sm text-black shrink-0 w-28 text-right">
                {String(t('central.platformPermission.organization.modal.warehouseList'))}:
              </label>
              <TreeSelect
                treeCheckable
                showCheckedStrategy={TreeSelect.SHOW_PARENT}
                treeExpandedKeys={warehouseExpandedKeys}
                onTreeExpand={(keys) => setWarehouseExpandedKeys(keys)}
                allowClear
                value={selectedWarehouseIds}
                placeholder={warehouseSelectPlaceholder}
                onChange={(vals) => handleWarehouseTreeChange(vals as Array<number | string | { value: number | string }>)}
                treeData={warehouseTreeDataForModal}
                loading={warehouseListLoading}
                treeNodeFilterProp="title"
                className="flex-1 min-w-0 rounded border-purple-200"
              />
            </div>
            </div>
          </Form>
        )}
      </Modal>

      <Modal
        {...baseModalProps}
        title={String(t('central.platformPermission.organization.modal.transferTitle'))}
        open={modal?.type === 'transfer'}
        footer={
          <div className="flex justify-end gap-2">
            <Button className="text-gray-700" onClick={closeModal}>
              {String(t('central.platformPermission.organization.modal.cancel'))}
            </Button>
            <HasResource resourceCodes={[ResourceCode.dept.TRANSFER]}>
              <Button
                type="primary"
                loading={mutating}
                disabled={transferTargetId == null}
                className={
                  'bg-purple-600 hover:!bg-purple-600 ' +
                  'disabled:!bg-[#f5f5f5] disabled:!border-[#d9d9d9] disabled:!text-[rgba(0,0,0,0.25)] ' +
                  'disabled:hover:!bg-[#f5f5f5] disabled:hover:!border-[#d9d9d9] disabled:hover:!text-[rgba(0,0,0,0.25)]'
                }
                onClick={confirmTransfer}
              >
                {String(t('central.platformPermission.organization.modal.transferConfirm'))}
              </Button>
            </HasResource>
          </div>
        }
      >
        {currentNode && (
          <div className="space-y-3 pt-2 max-w-[520px] mx-auto">
            <div className="flex flex-row items-center gap-2">
              <label className="text-sm text-black shrink-0 w-28 text-right">
                {t('central.platformPermission.organization.modal.transferCurrentTeam')}:
              </label>
              <div className="text-sm text-black">{currentNode.name}</div>
            </div>
            <div className="flex flex-row items-center gap-2">
              <label className="text-sm text-black shrink-0 w-28 text-right">
                {t('central.platformPermission.organization.modal.transferCurrentParent')}:
              </label>
              <div className="text-sm text-black">{currentParentNode ? currentParentNode.name : '-'}</div>
            </div>
            <div className="flex flex-row items-center gap-2">
              <label className="text-sm text-black shrink-0 w-28 text-right">
                {t('central.platformPermission.organization.modal.transferTargetParent')}:
              </label>
              <TreeSelect
                placeholder={String(t('components.input.select'))}
                value={transferTargetId}
                onChange={(v) => setTransferTargetId(v ?? null)}
                showSearch
                searchValue={transferTreeSearch}
                onSearch={setTransferTreeSearch}
                treeLine
                switcherIcon={renderPlusMinusSwitcher}
                treeNodeFilterProp="searchText"
                treeExpandedKeys={transferTreeExpandedKeys}
                onTreeExpand={(keys) => setTransferTreeExpandedKeys((keys as Array<string | number>).map(String))}
                treeData={transferTreeOptions}
                className="flex-1 min-w-0 rounded border-purple-200"
                allowClear
              />
            </div>
          </div>
        )}
      </Modal>

      <Modal
        {...baseModalProps}
        title={String(t('central.platformPermission.organization.modal.deleteTitle'))}
        open={modal?.type === 'delete'}
        footer={
          <div className="flex justify-end gap-2">
            <Button className="text-gray-700" onClick={closeModal}>
              {String(t('central.platformPermission.organization.modal.cancel'))}
            </Button>
            <HasResource resourceCodes={[ResourceCode.dept.DELETE]}>
              <Button
                type="primary"
                loading={mutating}
                className={
                  '!bg-red-500 !border-red-500 !text-white ' +
                  'hover:!bg-red-500 hover:!border-red-500 hover:!text-white ' +
                  'focus:!bg-red-500 focus:!border-red-500 focus:!text-white ' +
                  'active:!bg-red-500 active:!border-red-500 active:!text-white'
                }
                onClick={confirmDelete}
              >
                {String(t('central.platformPermission.organization.modal.deleteConfirm'))}
              </Button>
            </HasResource>
          </div>
        }
      >
        {currentNode && (
          <p className="pt-2 text-black">
            {t('central.platformPermission.organization.modal.deleteMessage', {
              node: currentNode.name,
              interpolation: { escapeValue: false },
            })}
          </p>
        )}
      </Modal>

      <Modal
        {...baseModalProps}
        title={String(t('central.platformPermission.organization.modal.addTitle'))}
        open={modal?.type === 'add'}
        footer={
          <div className="flex justify-end gap-2">
            <Button className="text-gray-700" onClick={closeModal}>
              {String(t('central.platformPermission.organization.modal.cancel'))}
            </Button>
            <HasResource resourceCodes={[ResourceCode.dept.ADD]}>
              <Button
                type="primary"
                className="bg-purple-600 hover:!bg-purple-700 border-purple-600"
                loading={mutating}
                onClick={confirmAdd}
              >
                {String(t('central.platformPermission.organization.modal.addConfirm'))}
              </Button>
            </HasResource>
          </div>
        }
      >
        {modal?.type === 'add' && (
          <Form form={addForm} component={false}>
            <div className="space-y-3 pt-2 max-w-[520px] mx-auto">
            <div className="flex flex-row items-center gap-2">
              <label className="text-sm text-black shrink-0 w-28 text-right">
                {t('central.platformPermission.organization.modal.addCurrentTeam')}:
              </label>
              <div className="text-sm text-black">
                {modal.nodeId === ROOT_ID
                  ? treeData.name
                  : currentNode
                    ? currentNode.name
                    : modal.nodeId}
              </div>
            </div>
            <div className="flex flex-row items-start gap-2">
              <label className="text-sm text-black shrink-0 w-28 text-right pt-[6px]">
                {t('central.platformPermission.organization.modal.addNewName')}:
              </label>
              <Form.Item
                name="name"
                rules={[{ required: true, message: String(t('central.platformPermission.organization.modal.renameNameRequired')) }]}
                className="mb-0 flex-1 min-w-0"
              >
                <Input
                  maxLength={100}
                  onBlur={() => {
                    const val = String(addForm.getFieldValue('name') ?? '').trim();
                    addForm.setFieldsValue({ name: val });
                  }}
                  placeholder={String(t('components.input.typeIn'))}
                  className="rounded border-purple-200"
                />
              </Form.Item>
            </div>
            <div className="flex flex-row items-center gap-2">
              <label className="text-sm text-black shrink-0 w-28 text-right">
                {String(t('central.platformPermission.organization.modal.warehouseList'))}:
              </label>
              <TreeSelect
                treeCheckable
                showCheckedStrategy={TreeSelect.SHOW_PARENT}
                treeExpandedKeys={warehouseExpandedKeys}
                onTreeExpand={(keys) => setWarehouseExpandedKeys(keys)}
                allowClear
                value={selectedWarehouseIds}
                placeholder={warehouseSelectPlaceholder}
                onChange={(vals) => handleWarehouseTreeChange(vals as Array<number | string | { value: number | string }>)}
                treeData={warehouseTreeDataForModal}
                loading={warehouseListLoading}
                treeNodeFilterProp="title"
                className="flex-1 min-w-0 rounded border-purple-200"
              />
            </div>
            </div>
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default OrganizationStructurePage;
